<script>
import BanobagiBottomNavigation, {
  BANOBAGI_BOTTOM_NAV_ROUTES
} from "@/components/banobagi/BanobagiBottomNavigation.vue";
import BanobagiTransactionDialogContent from "@/components/banobagi/transaction/BanobagiTransactionDialogContent.vue";
import { useDialog } from "@/services/dialogService";
import BanobagiHeader from "@/views/layout/BanobagiHeader.vue";
import { markRaw } from "vue";

const RawBanobagiTransactionDialogContent = markRaw(
  BanobagiTransactionDialogContent
);

export default {
  name: "BanobagiLayout",
  components: {
    BanobagiBottomNavigation,
    BanobagiHeader
  },
  inject: {
    isInsideBanobagiLayout: {
      default: false
    }
  },
  setup() {
    const dialog = useDialog();
    return { dialog };
  },
  data() {
    return {
      receiptRoute: "/receipt",
      isTransactionDialogOpen: false,
      transactionOpenSource: ""
    };
  },
  provide() {
    return {
      isInsideBanobagiLayout: true,
      isInsideStandardV2Layout: true,
      getBanobagiReceiptRoute: () => this.receiptRoute,
      openBanobagiTransactionDialog: options =>
        this.openTransactionDialog(options)
    };
  },
  created() {
    this.getReceiptRoute();
  },
  computed: {
    normalizedRoutePath() {
      const path = this.$route.path || "";
      if (path.length > 1 && path.endsWith("/")) return path.slice(0, -1);
      return path;
    },
    showBottomNavigation() {
      return BANOBAGI_BOTTOM_NAV_ROUTES.includes(this.normalizedRoutePath);
    },
    headerConfig() {
      return (
        this.$route.meta?.banobagiHeader ||
        this.$route.meta?.standardV2Header ||
        null
      );
    }
  },
  methods: {
    async getReceiptRoute() {
      try {
        const data = await this.$axios(
          "/api/v1/setting/display_language/customer_menu"
        );
        const receiptMenu = (data.detail || []).find(
          item =>
            item.display == 1 &&
            typeof item.menu_url === "string" &&
            item.menu_url.includes("/receipt")
        );

        if (receiptMenu) {
          this.receiptRoute = this.normalizeReceiptRoute(receiptMenu.menu_url);
        }
      } catch (error) {
        this.receiptRoute = "/receipt";
        this.$logger({
          response: error.response || {
            config: {
              url: "/api/v1/setting/display_language/customer_menu",
              methods: "GET",
              data: null
            }
          },
          action: {
            action: "load banobagi receipt route",
            id: 3,
            note: error.message
          }
        });
      }
    },
    async openTransactionDialog(options = {}) {
      if (this.isTransactionDialogOpen) return;

      this.isTransactionDialogOpen = true;
      this.transactionOpenSource = options.source || "";

      try {
        await this.dialog.showCustom(
          RawBanobagiTransactionDialogContent,
          {},
          {
            contentClass: "banobagi-transaction-overlay",
            width: "100%"
          }
        );
      } catch (error) {
        this.$logger({
          response: error.response || {
            config: {
              url: "dialog/banobagi-transaction",
              methods: "OPEN",
              data: JSON.stringify(options)
            }
          },
          action: {
            action: "open banobagi transaction dialog",
            id: 3,
            note: error.message
          }
        });
        await this.dialog.showError({ text: this.$t("error_message") });
      } finally {
        this.isTransactionDialogOpen = false;
        this.transactionOpenSource = "";
      }
    },
    normalizeReceiptRoute(value) {
      if (value.startsWith("/")) return value;

      try {
        const url = new URL(value, window.location.origin);
        if (url.origin === window.location.origin) {
          return `${url.pathname}${url.search}`;
        }
      } catch (_) {
        // Fall back to the local receipt entry point.
      }

      return "/receipt";
    },
    resolveHeaderTitle(headerConfig) {
      if (!headerConfig) return "";
      if (headerConfig.useBrandTitle) return this.$BRAND || "";
      if (headerConfig.titleKey) return this.$t(headerConfig.titleKey);
      return headerConfig.title || "";
    },
    handleHeaderAction(action) {
      if (action.key === "back") {
        this.$router.back();
        return;
      }

      if (action.to) {
        this.$router.push(action.to);
        return;
      }

      this.emitter.emit("banobagi-header-action", {
        action: action.key,
        route: this.$route.name
      });
    }
  }
};
</script>

<template>
  <slot v-if="isInsideBanobagiLayout" />
  <main v-else class="banobagi-layout" data-test="banobagi-layout">
    <BanobagiHeader
      v-if="headerConfig"
      :title="resolveHeaderTitle(headerConfig)"
      :show-back="headerConfig.showBack"
      :compact-back="headerConfig.compactBack"
      :emphasized="headerConfig.emphasized"
      :actions="headerConfig.actions"
      @action="handleHeaderAction"
    />

    <div
      :class="[
        'banobagi-layout__content',
        {
          'banobagi-layout__content--with-header': headerConfig,
          'banobagi-layout__content--with-navigation': showBottomNavigation
        }
      ]"
      data-test="banobagi-layout-content"
    >
      <slot />
    </div>

    <BanobagiBottomNavigation
      v-if="showBottomNavigation"
      :receipt-route="receiptRoute"
      :transaction-active="
        isTransactionDialogOpen && transactionOpenSource === 'orders'
      "
      @open-transaction="openTransactionDialog({ source: 'orders' })"
    />
  </main>
</template>

<style lang="scss" scoped>
.banobagi-layout {
  --banobagi-bottom-navigation-space: calc(
    92px + max(32px, env(safe-area-inset-bottom))
  );

  position: relative;
  width: 100%;
  max-width: 480px;
  min-height: 100dvh;
  margin: 0 auto;
  isolation: isolate;
}

.banobagi-layout__content {
  min-height: 100dvh;
  display: flex;
  flex-direction: column;
}

.banobagi-layout__content--with-header {
  min-height: calc(100dvh - 62px);
}

.banobagi-layout__content--with-navigation {
  padding-bottom: var(--banobagi-bottom-navigation-space);
}
</style>
